import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Users, Trash2, UserPlus, X } from 'lucide-react';
import api from '../lib/axios';

interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: 'ACCOUNTANT' | 'ADMIN' | 'SUPER_ADMIN';
  active: boolean;
}

type MemberForm = {
  email: string;
  name: string;
  role: Member['role'];
  password: string;
};

const EMPTY: MemberForm = { email: '', name: '', role: 'ACCOUNTANT', password: '' };
const ROLES: Member['role'][] = ['ACCOUNTANT', 'ADMIN', 'SUPER_ADMIN'];

export default function TenantMembersPanel({ slug, onClose }: Readonly<{ slug: string; onClose: () => void }>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<MemberForm>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['tenant-members', slug],
    queryFn: () => api.get<Member[]>(`/tenants/${slug}/members`).then((r) => r.data),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<Member>(`/tenants/${slug}/members`, {
        email: form.email.trim(),
        name: form.name.trim() || null,
        role: form.role,
        password: form.password.trim() || null,
      }),
    onSuccess: () => {
      setForm({ ...EMPTY });
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenant-members', slug] });
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message ?? t('members.addError')),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.delete(`/tenants/${slug}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-members', slug] }),
  });

  const changeRole = useMutation({
    mutationFn: (vars: { userId: string; role: Member['role'] }) =>
      api.patch(`/tenants/${slug}/members/${vars.userId}/role`, { role: vars.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-members', slug] }),
    onError: (e: any) => setError(e?.response?.data?.message ?? t('members.addError')),
  });

  const submit = () => {
    setError(null);
    if (!form.email.trim()) {
      setError(t('members.missingEmail'));
      return;
    }
    add.mutate();
  };

  const list = members.data ?? [];

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="reveal card mt-8 w-full max-w-2xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <Users size={14} /> {t('members.title')}
            </p>
            <h2 className="page-title mt-1 text-xl">{slug}</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={16} /> {t('common.close')}
          </button>
        </div>

        <div className="card mb-5 p-5">
          <p className="eyebrow flex items-center gap-2">
            <UserPlus size={14} /> {t('members.add')}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="field-label">{t('members.email')}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="input"
                placeholder="membre@association.fr"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label">{t('members.name')}</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="input"
                placeholder={t('members.namePlaceholder')}
              />
            </div>
            <div>
              <label className="field-label">{t('members.role')}</label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Member['role'] }))}
                className="input"
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {t(`members.roles.${role}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">{t('members.password')}</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="input"
                placeholder={t('members.passwordHint')}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button onClick={submit} disabled={add.isPending} className="btn btn-primary">
              {add.isPending ? t('members.adding') : t('members.add')}
            </button>
          </div>
        </div>

        {members.isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}
        {!members.isLoading && list.length === 0 && (
          <p className="text-sm text-ink-muted">{t('members.empty')}</p>
        )}

        {list.length > 0 && (
          <div className="card overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('members.email')}</th>
                  <th>{t('members.name')}</th>
                  <th>{t('members.role')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((member) => (
                  <tr key={member.userId}>
                    <td className="font-medium text-ink">{member.email}</td>
                    <td className="text-ink-soft">{member.name || t('common.none')}</td>
                    <td>
                      <select
                        value={member.role}
                        onChange={(e) =>
                          changeRole.mutate({ userId: member.userId, role: e.target.value as Member['role'] })
                        }
                        disabled={changeRole.isPending}
                        className="input !py-1"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {t(`members.roles.${role}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => remove.mutate(member.userId)}
                        disabled={remove.isPending}
                        className="btn btn-ghost"
                        title={t('members.remove')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
